import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import officeImg from '../assets/serveses/ChatGPT Image Aug 20, 2026, 12_23_46 PM.png';
import internImg from '../assets/serveses/ChatGPT Image Aug 20, 2026, 12_52_30 PM.png';

const perks = [
  { icon: '🚀', title: 'Fast Growth', desc: 'Accelerated learning curve working on live, real-world client projects from day one.' },
  { icon: '🧠', title: 'Smart Mentorship', desc: 'Work alongside senior engineers, architects, and founders who invest in your growth.' },
  { icon: '🌍', title: 'Remote Friendly', desc: 'Hybrid and remote-first culture. We trust you to deliver, wherever you are.' },
  { icon: '💡', title: 'Innovation Culture', desc: 'Contribute ideas, ship experiments, and see your suggestions become features.' },
  { icon: '🏆', title: 'Performance Rewards', desc: 'Transparent appraisal cycles with bonuses tied to real impact, not just tenure.' },
  { icon: '📚', title: 'Learning Budget', desc: 'Dedicated allowance for courses, certifications, books, and conferences.' },
];

const openings = [
  {
    id: 1,
    title: 'Full Stack Developer',
    dept: 'Engineering',
    type: 'Full-Time',
    mode: 'Hybrid',
    location: 'Coimbatore / Remote',
    skills: ['React', 'Node.js', 'MongoDB', 'AWS'],
    desc: 'Build and maintain scalable web applications for our diverse client base. You will own features end-to-end from DB schema to UI pixel.',
    gradient: 'from-brand-blue to-indigo-700',
    accent: '#1A3BCC',
  },
  {
    id: 2,
    title: 'AI / ML Engineer',
    dept: 'AI Research',
    type: 'Full-Time',
    mode: 'Remote',
    location: 'Remote India',
    skills: ['Python', 'PyTorch', 'LangChain', 'FastAPI'],
    desc: 'Design and deploy machine learning pipelines and LLM-powered applications for enterprise clients. Drive cutting-edge AI innovation.',
    gradient: 'from-violet-500 to-purple-700',
    accent: '#7C3AED',
  },
  {
    id: 3,
    title: 'UI/UX Designer',
    dept: 'Design',
    type: 'Full-Time',
    mode: 'Hybrid',
    location: 'Coimbatore',
    skills: ['Figma', 'Prototyping', 'Design Systems', 'Research'],
    desc: 'Craft beautiful, intuitive interfaces for products used by thousands. Own the design language from discovery to pixel-perfect delivery.',
    gradient: 'from-pink-500 to-rose-600',
    accent: '#E11D48',
  },
  {
    id: 4,
    title: 'Mobile App Developer',
    dept: 'Engineering',
    type: 'Full-Time',
    mode: 'Hybrid',
    location: 'Coimbatore / Remote',
    skills: ['React Native', 'Flutter', 'Firebase', 'REST APIs'],
    desc: 'Build high-performance cross-platform mobile apps. You will work on both consumer-facing and enterprise mobile products.',
    gradient: 'from-sky-500 to-cyan-600',
    accent: '#0EA5E9',
  },
  {
    id: 5,
    title: 'DevOps & Cloud Engineer',
    dept: 'Infrastructure',
    type: 'Full-Time',
    mode: 'Remote',
    location: 'Remote India',
    skills: ['AWS', 'Docker', 'Kubernetes', 'Terraform', 'CI/CD'],
    desc: 'Design, build, and maintain our cloud infrastructure. Ensure high availability, security, and zero-downtime deployments for client systems.',
    gradient: 'from-emerald-500 to-teal-600',
    accent: '#10B981',
  },
  {
    id: 6,
    title: 'Training Coordinator',
    dept: 'Education',
    type: 'Full-Time',
    mode: 'On-site',
    location: 'Coimbatore',
    skills: ['Communication', 'Curriculum Planning', 'LMS', 'Content Design'],
    desc: 'Manage end-to-end delivery of our college SDP and FDP programmes. Coordinate with institutions, trainers, and students for seamless execution.',
    gradient: 'from-orange-500 to-amber-600',
    accent: '#FF7B00',
  },
];

const depts = ['All', ...Array.from(new Set(openings.map(o => o.dept)))];

function JobCard({ job, index, onApply }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: index * 0.08, ease: [0.25, 0.46, 0.45, 0.94] }}
      className="group relative bg-white rounded-3xl border border-slate-200 shadow-card overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_48px_rgba(26,59,204,0.12)] transition-all duration-300"
    >
      {/* Left accent bar */}
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${job.gradient}`} />

      <div className="p-7 pl-9">
        {/* Top */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-space font-bold tracking-widest uppercase bg-slate-100 text-slate-500">{job.dept}</span>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-space font-bold tracking-widest uppercase" style={{ backgroundColor: job.accent + '15', color: job.accent }}>{job.type}</span>
              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-space font-bold tracking-widest uppercase bg-slate-100 text-slate-500">{job.mode}</span>
            </div>
            <h3 className="font-syne font-bold text-xl text-slate-900 group-hover:text-brand-blue transition-colors duration-200">{job.title}</h3>
            <p className="font-inter text-xs text-slate-400 mt-1 flex items-center gap-1">
              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
              {job.location}
            </p>
          </div>
        </div>

        <p className="font-inter text-slate-500 text-sm leading-relaxed mb-5">{job.desc}</p>

        {/* Skills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {job.skills.map((s, i) => (
            <span key={i} className="px-3 py-1 rounded-full bg-slate-50 border border-slate-200 text-xs font-space font-medium text-slate-700">{s}</span>
          ))}
        </div>

        {/* CTA */}
        <button
          onClick={() => onApply(job)}
          className="w-full py-3 rounded-2xl font-space font-bold text-sm text-white transition-all duration-300 hover:opacity-90 hover:shadow-lg"
          style={{ background: `linear-gradient(135deg, ${job.accent}, ${job.accent}cc)` }}
        >
          Apply Now
        </button>
      </div>
    </motion.div>
  );
}

function ApplyModal({ job, onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);
  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0, y: 24 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.92, opacity: 0, y: 24 }}
        transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        <div className={`bg-gradient-to-r ${job.gradient} p-8`}>
          <button onClick={onClose} className="float-right text-white/60 hover:text-white transition-colors">
            <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
          <h3 className="font-syne font-bold text-2xl text-white mb-1">Apply for</h3>
          <p className="font-syne font-extrabold text-3xl text-white">{job.title}</p>
          <p className="font-inter text-white/70 text-sm mt-2">{job.location} · {job.type}</p>
        </div>
        <div className="p-8">
          <div className="space-y-4">
            {[
              { label: 'Full Name', type: 'text', placeholder: 'Your full name' },
              { label: 'Email Address', type: 'email', placeholder: 'you@example.com' },
              { label: 'Phone Number', type: 'tel', placeholder: '+91 00000 00000' },
              { label: 'Portfolio / LinkedIn URL', type: 'url', placeholder: 'https://...' },
            ].map((f, i) => (
              <div key={i}>
                <label className="block font-space font-semibold text-xs text-slate-600 mb-1.5 uppercase tracking-wider">{f.label}</label>
                <input
                  type={f.type}
                  placeholder={f.placeholder}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-inter text-slate-800 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 transition-all"
                />
              </div>
            ))}
            <div>
              <label className="block font-space font-semibold text-xs text-slate-600 mb-1.5 uppercase tracking-wider">Cover Note (optional)</label>
              <textarea rows={3} placeholder="Why do you want to join QuBright?" className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-inter text-slate-800 focus:outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/10 transition-all resize-none" />
            </div>
          </div>
          <button
            className="mt-6 w-full py-4 rounded-2xl font-space font-bold text-sm text-white transition-all duration-300 hover:opacity-90 hover:shadow-lg"
            style={{ background: `linear-gradient(135deg, ${job.accent}, ${job.accent}cc)` }}
          >
            Submit Application 🚀
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Careers() {
  const navigate = useNavigate();
  const [activeDept, setActiveDept] = useState('All');
  const [applyJob, setApplyJob] = useState(null);

  const filtered = activeDept === 'All' ? openings : openings.filter(o => o.dept === activeDept);

  return (
    <div className="min-h-screen bg-white">
      <div className="relative z-50"><Navbar forceOpaque={true} /></div>

      {/* ── HERO ── */}
      <div className="relative overflow-hidden">
        {/* Split layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Left — content */}
          <div className="flex flex-col justify-center px-10 md:px-16 pt-28 pb-20 relative z-10 bg-white">
            <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none" />
            <div className="relative">
              <motion.button
                initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.3 }}
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-brand-blue font-space font-semibold text-sm mb-8 hover:gap-3 transition-all duration-200"
              >
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6"/></svg>
                Back to Home
              </motion.button>

              <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.2, duration: 0.75 }}>
                <span className="section-label inline-block mb-4">We are Hiring</span>
                <p className="font-space text-brand-orange font-semibold text-sm tracking-wide mb-3 italic">"Where Creativity Meets Precision"</p>
                <h1 className="font-syne font-extrabold text-5xl md:text-6xl text-slate-900 leading-tight mb-5">
                  Build the Future<br />
                  <span className="gradient-text-blue">with QuBright</span>
                </h1>
                <p className="font-inter text-slate-500 text-lg leading-relaxed max-w-lg mb-8">
                  Join a team of builders, thinkers, and creators who are shaping the future of
                  technology and education in India. <span className="font-semibold text-slate-700">Innovate. Build. Inspire.</span>
                </p>

                <div className="flex flex-wrap gap-4 mb-10">
                  <a href="#openings">
                    <button className="btn-primary">View Open Roles</button>
                  </a>
                  <a href="#contact">
                    <button className="btn-secondary">Send Open Application</button>
                  </a>
                </div>

                {/* Mini stats */}
                <div className="flex gap-8">
                  {[{ n: '25+', l: 'Team Members' }, { n: '3+', l: 'Years Old' }, { n: '4.8★', l: 'Team Rating' }].map((s, i) => (
                    <div key={i}>
                      <p className="font-syne font-extrabold text-2xl text-slate-900">{s.n}</p>
                      <p className="font-inter text-xs text-slate-400">{s.l}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>

          {/* Right — image */}
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, duration: 0.9, ease: 'easeOut' }}
            className="relative overflow-hidden min-h-[60vh] lg:min-h-0"
          >
            <img
              src={officeImg}
              alt="QuBright Team at Work"
              className="w-full h-full object-cover object-top absolute inset-0"
              style={{ minHeight: '100%' }}
            />
            {/* Dark overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 via-transparent to-slate-900/20" />
            {/* Floating badge */}
            <motion.div
              initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7 }}
              className="absolute bottom-10 left-10 bg-white/95 backdrop-blur-md rounded-2xl px-5 py-4 shadow-xl border border-slate-100"
            >
              <p className="font-space text-xs text-slate-400 uppercase tracking-widest mb-1">Our Culture</p>
              <p className="font-syne font-bold text-slate-900 text-sm">Code. Create. Collaborate. Celebrate.</p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* ── PERKS ── */}
      <div className="py-20 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="section-label inline-block mb-4">Why QuBright</span>
            <h2 className="font-syne font-extrabold text-3xl md:text-4xl text-slate-900">
              A workplace that <span className="gradient-text-blue">actually invests in you</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {perks.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.5 }}
                className="bg-white rounded-3xl p-7 border border-slate-200 shadow-card hover:-translate-y-1 hover:border-brand-blue/20 transition-all duration-300 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-brand-blue/8 flex items-center justify-center mb-5 text-2xl group-hover:scale-110 transition-transform duration-300">{p.icon}</div>
                <h4 className="font-syne font-bold text-slate-900 text-lg mb-2">{p.title}</h4>
                <p className="font-inter text-slate-500 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ── INTERNSHIP SECTION ── */}
      <div className="bg-slate-50 border-t border-slate-100">

        {/* Full-width image banner */}
        <div className="relative h-[420px] md:h-[520px] overflow-hidden">
          <img
            src={internImg}
            alt="QuBright intern team collaborating"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          {/* Dark gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/50 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />

          {/* Overlay text */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative z-10 h-full flex flex-col justify-center px-10 md:px-20 max-w-3xl"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-sky-400/20 border border-sky-400/30 text-sky-300 font-space font-bold text-xs tracking-widest uppercase mb-5">
              For Students
            </span>
            <h2 className="font-syne font-extrabold text-4xl md:text-5xl text-white leading-tight mb-4">
              Kickstart your career<br />
              <span className="text-sky-400">with QuBright Internships</span>
            </h2>
            <p className="font-inter text-white/75 text-base md:text-lg leading-relaxed max-w-xl">
              Join a real team. Work on live projects. Build the skills that matter.
              Not a shadowing exercise — you contribute from day one.
            </p>
          </motion.div>
        </div>

        {/* Stats strip */}
        <div className="bg-slate-900">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-700">
              {[
                { n: '6', l: 'Technology Tracks' },
                { n: '1–6', l: 'Month Duration' },
                { n: '100%', l: 'Real Client Projects' },
                { n: '🏅', l: 'Stipend for Top Interns' },
              ].map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="py-6 px-8 text-center"
                >
                  <p className="font-syne font-extrabold text-2xl md:text-3xl text-white mb-1">{s.n}</p>
                  <p className="font-inter text-slate-400 text-xs">{s.l}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Split: content left, image accent right */}
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">

            {/* Left — details */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.65 }}
            >
              <h3 className="font-syne font-extrabold text-3xl text-slate-900 mb-6">
                What you get as a <span className="gradient-text-blue">QuBright Intern</span>
              </h3>
              <div className="space-y-4 mb-8">
                {[
                  { icon: '👨‍💻', title: 'Work on real client projects', desc: 'Build and ship features that real users interact with, across web, mobile, AI and cloud.' },
                  { icon: '🎓', title: 'Weekly mentorship & reviews', desc: 'Get structured 1-on-1 feedback from senior engineers every single week.' },
                  { icon: '📜', title: 'Industry-recognised certificate', desc: 'Earn a completion certificate valued by top companies across India.' },
                  { icon: '🔗', title: 'LinkedIn recommendation', desc: 'Outstanding performers receive personal LinkedIn endorsements from founders.' },
                  { icon: '⭐', title: 'Priority for full-time roles', desc: 'Top interns get first consideration when we open full-time positions.' },
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.07, duration: 0.4 }}
                    className="flex gap-4 p-4 rounded-2xl hover:bg-slate-100 transition-colors duration-200"
                  >
                    <span className="text-2xl flex-shrink-0 mt-0.5">{item.icon}</span>
                    <div>
                      <p className="font-syne font-bold text-slate-900 text-base mb-0.5">{item.title}</p>
                      <p className="font-inter text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
              <a href="mailto:qubrightoffice@gmail.com">
                <button className="btn-primary">Apply for Internship</button>
              </a>
            </motion.div>

            {/* Right — image card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src={internImg}
                alt="QuBright interns collaborating"
                className="w-full h-[480px] object-cover object-center"
              />
              {/* Bottom caption bar */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900/95 via-slate-900/60 to-transparent p-8">
                <p className="font-space text-sky-400 text-xs uppercase tracking-widest mb-2">Technology · Innovation · Impact</p>
                <p className="font-syne font-bold text-white text-xl">Join a team that builds things that matter.</p>
              </div>
            </motion.div>

          </div>
        </div>
      </div>

      {/* ── OPENINGS ── */}
      <div id="openings" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="section-label inline-block mb-4">Open Positions</span>
            <h2 className="font-syne font-extrabold text-3xl md:text-4xl text-slate-900 mb-3">
              Current <span className="gradient-text-orange">Openings</span>
            </h2>
          </div>

          {/* ── No openings notice ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center py-16 px-10 rounded-3xl bg-slate-50 border border-slate-200 shadow-card"
          >
            <div className="w-16 h-16 rounded-2xl bg-brand-blue/8 flex items-center justify-center mx-auto mb-6 text-3xl">🔭</div>
            <h3 className="font-syne font-bold text-2xl text-slate-900 mb-3">No Openings Right Now</h3>
            <p className="font-inter text-slate-500 text-base leading-relaxed mb-2">
              We don't have any active positions at the moment, but we are always on the lookout
              for exceptional talent.
            </p>
            <p className="font-inter text-slate-400 text-sm mb-8">
              Send us your resume and we'll reach out when the right opportunity arises.
            </p>
            <a href="mailto:qubrightoffice@gmail.com">
              <button className="btn-primary">Send Open Application</button>
            </a>
          </motion.div>

          {/* Job cards — hidden until openings are available. Change false to true to re-enable. */}
          {false && (
            <AnimatePresence mode="wait">
              <motion.div
                key={activeDept}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
              >
                {filtered.map((job, i) => (
                  <JobCard key={job.id} job={job} index={i} onApply={setApplyJob} />
                ))}
              </motion.div>
            </AnimatePresence>
          )}

        </div>
      </div>


      <Footer />

      {/* Apply Modal */}
      <AnimatePresence>
        {applyJob && <ApplyModal key="modal" job={applyJob} onClose={() => setApplyJob(null)} />}
      </AnimatePresence>
    </div>
  );
}
